/**
 * Project and preset actions on top of LocalStore. Never overwrites silently:
 * conflicts surface as errors, imports with a colliding id get a new identity.
 */
import { AppError, errorMessage } from "../core/errors";
import { createId } from "../core/ids";
import { createDefaultProject } from "../project/defaults";
import { PRESET_FORMAT, type Preset, type Project } from "../project/schema";
import { parsePreset, parseProject, serializeProject, withNewIdentity } from "../project/serialize";
import { requestPersistentStorage, type ProjectSummary } from "../project/storage";
import type { EngineController } from "./controller";

export class Library {
  private persistRequested = false;

  constructor(private c: EngineController) {}

  private get store() {
    if (!this.c.storage) throw new AppError("STORAGE_ERROR", "Local storage (IndexedDB) is unavailable in this browser session.");
    return this.c.storage;
  }

  async listProjects(): Promise<ProjectSummary[]> {
    return this.c.storage ? this.c.storage.listProjects() : [];
  }

  async save(): Promise<boolean> {
    return this.guard(async () => {
      const saved = await this.store.saveProject(this.c.project.get());
      this.c.markSaved(saved);
      await this.requestPersist();
      this.c.notify("info", `Saved "${saved.name}".`);
    });
  }

  async saveAsCopy(name: string): Promise<boolean> {
    return this.guard(async () => {
      const copy = withNewIdentity({ ...this.c.project.get(), name: name.trim() || "Untitled copy" });
      const saved = await this.store.saveProject(copy);
      this.c.loadProject(saved);
      await this.requestPersist();
      this.c.notify("info", `Saved a copy as "${saved.name}".`);
    });
  }

  async open(id: string): Promise<boolean> {
    return this.guard(async () => this.c.loadProject(await this.store.loadProject(id)));
  }

  newProject(name = "Untitled project"): void {
    this.c.loadProject(createDefaultProject(name), { dirty: false });
  }

  async remove(id: string): Promise<boolean> {
    return this.guard(() => this.store.deleteProject(id));
  }

  exportCurrent(): { filename: string; json: string } {
    const p = this.c.project.get();
    const safe = p.name.replace(/[^a-z0-9-_ ]/gi, "").trim().replace(/\s+/g, "-") || "project";
    return { filename: `${safe}.project.json`, json: serializeProject(p) };
  }

  /** Validate and open an imported project. Asset binaries are not embedded in v1 JSON. */
  async importJson(text: string): Promise<boolean> {
    return this.guard(async () => {
      let p: Project = parseProject(text);
      if (this.c.storage && (await this.c.storage.hasProject(p.id))) {
        p = withNewIdentity(p);
        this.c.notify("info", "A project with the same id already exists, so the import was opened as a new copy.");
      }
      this.c.loadProject({ ...p, revision: 0 }, { dirty: true });
    });
  }

  // ----------------------------------------------------------------- presets

  async listPresets(kind: Preset["instrument"]["kind"]): Promise<Preset[]> {
    return this.c.storage ? this.c.storage.listPresets(kind) : [];
  }

  async savePresetFromTrack(trackId: string, name: string): Promise<Preset | null> {
    let out: Preset | null = null;
    await this.guard(async () => {
      const t = this.c.project.get().tracks.find((x) => x.id === trackId);
      if (!t) throw new AppError("NOT_FOUND", "Track not found.");
      const now = new Date().toISOString();
      if (t.instrument.kind === "multisample") throw new AppError("FILE_INVALID", "Presets are not available for built-in sampled instruments yet.");
      const instrument = t.instrument.kind === "sampler" ? { kind: "sampler" as const, params: t.instrument.params } : t.instrument;
      out = await this.store.savePreset({ format: PRESET_FORMAT, schemaVersion: 1, id: createId(), name: name.trim() || "Preset", tags: [], createdAt: now, updatedAt: now, instrument });
      this.c.notify("info", `Preset "${out.name}" saved.`);
    });
    return out;
  }

  async duplicatePreset(p: Preset): Promise<boolean> {
    return this.guard(async () => {
      const now = new Date().toISOString();
      await this.store.savePreset({ ...p, id: createId(), name: `${p.name} copy`.slice(0, 120), createdAt: now, updatedAt: now });
    });
  }

  async renamePreset(p: Preset, name: string): Promise<boolean> {
    return this.guard(async () => { await this.store.savePreset({ ...p, name: name.trim() || p.name, updatedAt: new Date().toISOString() }); });
  }

  async deletePreset(id: string): Promise<boolean> {
    return this.guard(() => this.store.deletePreset(id));
  }

  /** Apply a preset's params to a track with the same instrument kind. */
  applyPreset(trackId: string, preset: Preset): void {
    this.c.updateProject((proj) => ({
      ...proj,
      tracks: proj.tracks.map((t) => {
        if (t.id !== trackId || t.instrument.kind !== preset.instrument.kind) return t;
        return { ...t, instrument: { ...t.instrument, params: { ...preset.instrument.params } } as typeof t.instrument };
      }),
    }));
  }

  exportPreset(p: Preset): { filename: string; json: string } {
    return { filename: `${p.name.replace(/[^a-z0-9-_ ]/gi, "").trim() || "preset"}.preset.json`, json: JSON.stringify(p, null, 2) };
  }

  async importPreset(text: string): Promise<boolean> {
    return this.guard(async () => {
      const p = parsePreset(text);
      await this.store.savePreset({ ...p, id: createId() });
      this.c.notify("info", `Imported preset "${p.name}".`);
    });
  }

  private async requestPersist(): Promise<void> {
    if (this.persistRequested) return;
    this.persistRequested = true;
    const r = await requestPersistentStorage();
    if (r === "denied") this.c.notify("warning", "The browser did not grant persistent storage; local projects could be evicted under storage pressure. Export important projects.");
  }

  private async guard(fn: () => Promise<unknown> | unknown): Promise<boolean> {
    try { await fn(); return true; } catch (err) { this.c.notify("error", errorMessage(err)); return false; }
  }
}
